<script setup>
import { computed, onBeforeUnmount } from 'vue'

import { CChannelMessages } from '@/components/channels/organisms'
import { useDateFormatter } from '@/libs/date'

const { formatDate } = useDateFormatter()
import { onMounted } from 'vue'

import { useStore } from '@/wrappers/store'

const { mapActions } = useStore()

const { clearState, setRawCurrentChannel } = mapActions('pillars/channel')

const props = defineProps({
  channel: { type: Object, default: () => ({}) }
})

const staffArray = computed(() => Object.values(props.channel.staff))

const lastStaffRead = () => {
  const staffViews = staffArray.value
    .filter(staff => staff.unreadMessages === null)
    .map(staff => staff.name)

  if (staffViews.length > 0) {
    return 'Visto por ' + staffViews.join(', ')
  } else {
    return 'Mensagens não lidas'
  }
}
const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + '...'
  }
  return text
}

onMounted(() => {
  setRawCurrentChannel(props.channel)
})

onBeforeUnmount(() => {
  clearState()
})
</script>

<template>
  <div v-if="channel.id" class="chat">
    <div class="chat__header">
      <div class="header__content">
        <div class="channel__info">
          <span class="channel__name">{{
            truncateText(channel.name, 30)
          }}</span>
          <span class="separator">•</span>
          <span class="header__date">
            {{
              channel.createdAt
                ? formatDate(channel.createdAt)
                : 'Data não disponível'
            }}
          </span>
        </div>
        <div class="header__visualization">
          {{ truncateText(lastStaffRead(), 30) }}
        </div>
      </div>
    </div>
    <div class="chat__container">
      <CChannelMessages
        ref="messages"
        :current-channel="channel"
        class="chat__messages"
        only-view
      />
    </div>
  </div>
  <div v-else class="error">
    <div class="message">Esse canal não existe</div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/style/_non-rendering';

:root,
[data-theme='on-light'] {
  --channel-background-color: var(--gl-color-shades-white);
  --message-staff-background: var(--sys-color-surface-background-light);
  --message-other-background: var(--gl-color-shades-gray-10);
  --message-staff-color: var(--sys-color-content-primary);
  --message-staff-border-color: var(--sys-color-stroke-active);
  --split-bar-background-color: var(--channel-background-color);
  --split-bar-background: var(--channel-background-color);
  --split-bar-color: var(--gl-color-shades-gray-60);
  --split-bar-border-color: var(--gl-color-shades-gray-20);
  --message-visualization-color: var(--gl-color-shades-gray-60);
}

.error {
  width: 100%;
  height: 100%;

  .message {
    margin-top: 50%;
    text-align: center;
  }
}

.chat {
  background-color: var(--channel-background-color);

  &__container {
    display: grid;
    grid-template-rows: 1fr auto;
    min-height: 500px;
    max-height: 500px;

    &--callout {
      margin: var(--gl-spacing-06);
    }
  }
}

.chat__header {
  padding: 8px 16px;
  background-color: var(--sys-color-surface-container-hover);
  border-bottom: 1px solid var(--split-bar-border-color);

  .header__content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .channel__info {
      display: flex;
      align-items: center;

      .channel__name {
        font-size: var(--gl-font-size-xs);
        font-weight: var(--gl-font-weight-semibold);
      }

      .separator {
        font-size: var(--gl-font-size-xxs);
        margin: 0 8px;
        font-weight: var(--gl-font-weight-regular);
      }

      .header__date {
        font-size: var(--gl-font-size-xs);
        font-weight: var(--gl-font-weight-regular);
      }
    }

    .header__visualization {
      font-size: var(--gl-font-size-xxxs);
    }
  }
}
</style>
